/**
 * MatterMLCOPanel.jsx: BM-H3681 (5 Oct 2026).
 *
 * Vanessa: "do my decisions etc now get recorded on the matter/pipeline?"
 * They were saved (the item's history in the MLCO Inbox and a row in
 * matter_activity_log) but nothing on the matter page showed them. This
 * panel lists the matter's MLCO Inbox items, each with its state and
 * decision, and opens the same drawer as the inbox for the full history.
 *
 * MLCO and COLP only (Vanessa's choice on the decision card, 5 Oct 2026):
 * the outcome of an MLCO review must not reach the team (tipping off). The
 * list endpoint enforces the same gate (_require_mlco_or_colp), so a fee
 * earner who forces the panel open still gets nothing.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldAlert } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import DetailDrawer, {
  SEVERITY_BADGE, authHeaders, formatDate, itemStatusBadge, itemStatusLabel, outcomeLabel,
} from "./MLCOItemDrawer";

export default function MatterMLCOPanel({ matterRef }) {
  const [userMeta, setUserMeta] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const allowed = !!(userMeta?.is_mlco || userMeta?.is_colp);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user-role`, { headers: authHeaders() });
        if (!cancelled) setUserMeta(res.data || {});
      } catch (err) {
        // Not knowing the role means not showing MLCO material: the safe side.
        if (!cancelled) setUserMeta({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!matterRef) return;
    try {
      const r = await axios.get(`${API}/compliance/mlco-queue`, {
        params: { matter_ref: matterRef, limit: 50 },
        headers: authHeaders(),
      });
      setItems(r.data?.items || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load this matter's MLCO Inbox items"));
    }
  }, [matterRef]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  if (!allowed || !matterRef) return null;
  // Nothing has ever gone to the MLCO on this matter: say nothing.
  if (items && items.length === 0 && !error) return null;

  return (
    <div className="rounded-md border border-amber-800/50 bg-amber-950/20 p-3 space-y-2" data-testid="matter-mlco-panel">
      <div className="flex items-center gap-2 text-sm text-amber-200">
        <ShieldAlert className="w-4 h-4" />
        <span>MLCO Inbox (only the MLCO and COLP see this)</span>
      </div>
      {error && <div className="text-xs text-amber-300" data-testid="matter-mlco-error">{error}</div>}
      {items === null && !error && <div className="text-xs text-slate-400">Loading…</div>}
      {(items || []).map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => setSelected(it.id)}
          className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-800/60"
          data-testid={`matter-mlco-row-${it.id}`}
        >
          <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] border ${SEVERITY_BADGE[it.severity] || ""}`}>{it.severity}</span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm text-slate-100" data-testid={`matter-mlco-decision-${it.id}`}>
              {it.mlco_outcome
                ? `${outcomeLabel(it.mlco_outcome)}${it.mlco_actioned_by ? ` by ${it.mlco_actioned_by}` : ""}${it.mlco_actioned_at ? ` on ${formatDate(it.mlco_actioned_at)}` : ""}`
                : "No MLCO decision yet"}
            </span>
            <span className="block text-xs text-slate-400">Raised {formatDate(it.created_at)}. Open for the full history.</span>
          </span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] border ${itemStatusBadge(it)}`}>{itemStatusLabel(it)}</span>
        </button>
      ))}
      {selected && (
        <DetailDrawer itemId={selected} userMeta={userMeta} onClose={() => setSelected(null)} onActed={load} />
      )}
    </div>
  );
}
